/**
 * pdfToImage - Render the first page of a PDF to a data URL image
 *
 * Uses pdfjs-dist to render in a temporary canvas.
 * Returns a PNG data URL that can be used as a Fabric.js background.
 */

import * as pdfjsLib from 'pdfjs-dist';

// Configure worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

/**
 * Convert a PDF URL to an image data URL.
 *
 * Computes a render scale that fits the page within `maxDimension` pixels
 * (default 2400) on its longest side. This avoids generating a 6000×8000
 * PNG (~50MB base64) for large PDFs while still providing enough resolution
 * for clear annotation overlays.
 *
 * @param {string} pdfUrl - URL of the PDF file
 * @param {object} options
 * @param {number} options.maxDimension - Max pixel dimension on longest side (default 2400)
 * @returns {Promise<string>} PNG data URL
 */
export async function pdfToImageUrl(pdfUrl, options = {}) {
  const maxDimension = options.maxDimension || 2400;
  const minDimension = options.minDimension || 1800; // floor for analysis quality
  const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
  const page = await pdf.getPage(1);

  // Compute scale to fit longest side within maxDimension (capping huge PDFs)
  // but also ensuring it's at least minDimension (for plans where the PDF's
  // natural pixel size is small — Gemini needs sharp text/walls to detect rooms).
  const baseViewport = page.getViewport({ scale: 1 });
  const longSide = Math.max(baseViewport.width, baseViewport.height);
  const targetLongSide = Math.max(minDimension, Math.min(maxDimension, longSide * 2));
  const scale = targetLongSide / longSide;

  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: canvas.getContext('2d'),
    viewport,
  }).promise;

  return canvas.toDataURL('image/png');
}
